import React, { useEffect, useState } from "react";
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useConvexAuth, useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { MainRoutes } from "../../../../shared/models";
import { Button } from "../../../../catalyst-components/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../catalyst-components/table";
import { Alert, AlertActions, AlertDescription, AlertTitle } from "../../../../catalyst-components/alert";
import { Field, Label } from "../../../../catalyst-components/fieldset";
import { Textarea } from "../../../../catalyst-components/textarea";
import { Id } from "../../../../convex/_generated/dataModel";
import { Input } from "../../../../catalyst-components/input";
import { usePersistedForm } from "../../../../shared/hooks/usePersistedForm";

export const AssistantAdminMetadata: React.FC = () => {
    const { isAuthenticated } = useConvexAuth();
    const navigate = useNavigate();
    const adminMetadata = useQuery(api.assistant_admin_metadata.get);
    const updateAdminMetadata = useMutation(api.assistant_admin_metadata.update);
    const getOrCreateAdminMetadata = useMutation(api.assistant_admin_metadata.getOrCreate);
    const updateAssistantInstructions = useMutation(api.assistants.updateInstructions);
    const assistants = useQuery(api.assistants.list) || [];
    
    const [formState, updateFormState] = usePersistedForm('assistant-admin-metadata', {
        header: "",
        midpoint: "",
        body: "",
        footer: "",
        nameFilter: "",
        selectedAssistants: {} as {[key: string]: boolean}
    });

    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [updating, setUpdating] = useState(false);

    const filteredAssistants = assistants.filter(assistant => 
        assistant.name.toLowerCase().includes(formState.nameFilter.toLowerCase())
    );

    const selectedCount = Object.values(formState.selectedAssistants).filter(Boolean).length;

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(MainRoutes.Landing);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (adminMetadata) {
            updateFormState({
                ...formState,
                header: adminMetadata.header || "",
                midpoint: adminMetadata.midpoint || "",
                body: adminMetadata.body || "",
                footer: adminMetadata.footer || ""
            });
        }
    }, [adminMetadata]);

    const handleSaveHeader = async () => {
        try {
            let metadata = adminMetadata;
            if (!metadata) {
                metadata = await getOrCreateAdminMetadata();
            }
            
            await updateAdminMetadata({
                id: metadata._id,
                header: formState.header
            });
            
            setAlertMessage('Header saved successfully');
            setShowAlert(true);
            setSuccess(true);
        } catch (error) {
            setAlertMessage('Error saving header');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    const handleSaveFooter = async () => {
        try {
            let metadata = adminMetadata;
            if (!metadata) {
                metadata = await getOrCreateAdminMetadata();
            }
            
            await updateAdminMetadata({
                id: metadata._id,
                footer: formState.footer
            });
            
            setAlertMessage('Footer saved successfully');
            setShowAlert(true);
            setSuccess(true);
        } catch (error) {
            setAlertMessage('Error saving footer');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    const handleApplyHeaderToAssistants = async () => {
        try {
            setUpdating(true);
            
            const selectedAssistantIds = Object.entries(formState.selectedAssistants)
                .filter(([_, isSelected]) => isSelected)
                .map(([id]) => id);
                
            if (selectedAssistantIds.length === 0) {
                setAlertMessage('Please select at least one assistant');
                setShowAlert(true);
                setSuccess(false);
                setUpdating(false);
                return;
            }
            
            const allAssistantsToUpdate = [
                ...assistants.filter(a => selectedAssistantIds.includes(a._id))
            ];
            
            for (const assistant of allAssistantsToUpdate) {
                await updateAssistantInstructions({
                    assistantId: assistant._id as Id<"assistants">,
                    header: formState.header
                });
            }
            
            setAlertMessage(`Successfully updated header in ${allAssistantsToUpdate.length} assistants`);
            setShowAlert(true);
            setSuccess(true);
            updateFormState({ ...formState, selectedAssistants: {} });
        } catch (error) {
            setAlertMessage('Error applying header to assistants');
            setShowAlert(true);
            setSuccess(false);
        } finally {
            setUpdating(false);
        }
    };

    const handleApplyFooterToAssistants = async () => {
        try {
            setUpdating(true);
            
            const selectedAssistantIds = Object.entries(formState.selectedAssistants)
                .filter(([_, isSelected]) => isSelected)
                .map(([id]) => id);
                
            if (selectedAssistantIds.length === 0) {
                setAlertMessage('Please select at least one assistant');
                setShowAlert(true);
                setSuccess(false);
                setUpdating(false);
                return;
            }
            
            const allAssistantsToUpdate = [
                ...assistants.filter(a => selectedAssistantIds.includes(a._id))
            ];
            
            for (const assistant of allAssistantsToUpdate) {
                await updateAssistantInstructions({
                    assistantId: assistant._id as Id<"assistants">,
                    footer: formState.footer
                });
            }
            
            setAlertMessage(`Successfully updated footer in ${allAssistantsToUpdate.length} assistants`);
            setShowAlert(true);
            setSuccess(true);
            updateFormState({ ...formState, selectedAssistants: {} });
        } catch (error) {
            setAlertMessage('Error applying footer to assistants');
            setShowAlert(true);
            setSuccess(false);
        } finally {
            setUpdating(false);
        }
    };

    const handleApplyMidpointToAssistants = async () => {
        try {
            setUpdating(true);
            
            const selectedAssistantIds = Object.entries(formState.selectedAssistants)
                .filter(([_, isSelected]) => isSelected)
                .map(([id]) => id);
                
            if (selectedAssistantIds.length === 0) {
                setAlertMessage('Please select at least one assistant');
                setShowAlert(true);
                setSuccess(false);
                setUpdating(false);
                return;
            }
            
            const allAssistantsToUpdate = [
                ...assistants.filter(a => selectedAssistantIds.includes(a._id))
            ];
            
            for (const assistant of allAssistantsToUpdate) {
                await updateAssistantInstructions({
                    assistantId: assistant._id as Id<"assistants">,
                    midpoint: formState.midpoint
                });
            }
            
            setAlertMessage(`Successfully updated midpoint in ${allAssistantsToUpdate.length} assistants`);
            setShowAlert(true);
            setSuccess(true);
            updateFormState({ ...formState, selectedAssistants: {} });
        } catch (error) {
            setAlertMessage('Error applying midpoint to assistants');
            setShowAlert(true);
            setSuccess(false);
        } finally {
            setUpdating(false);
        }
    };

    const handleApplyEverythingToAssistants = async () => {
        try {
            setUpdating(true);
            
            const selectedAssistantIds = Object.entries(formState.selectedAssistants)
                .filter(([_, isSelected]) => isSelected)
                .map(([id]) => id);
                
            if (selectedAssistantIds.length === 0) {
                setAlertMessage('Please select at least one assistant');
                setShowAlert(true);
                setSuccess(false);
                setUpdating(false);
                return;
            }
            
            const allAssistantsToUpdate = [
                ...assistants.filter(a => selectedAssistantIds.includes(a._id))
            ];
            
            let updatedCount = 0;
            
            for (const assistant of allAssistantsToUpdate) {
                await updateAssistantInstructions({
                    assistantId: assistant._id as Id<"assistants">,
                    header: formState.header,
                    footer: formState.footer,
                    midpoint: formState.midpoint
                });
                updatedCount++;
            }
            
            setAlertMessage(`Successfully updated ${updatedCount} assistants`);
            setShowAlert(true);
            setSuccess(true);
            updateFormState({ ...formState, selectedAssistants: {} });
        } catch (error) {
            setAlertMessage('Error applying changes to assistants');
            setShowAlert(true);
            setSuccess(false);
        } finally {
            setUpdating(false);
        }
    };

    const handleSelectAll = (selected: boolean) => {
        const newSelection: {[key: string]: boolean} = {};
        assistants.forEach(assistant => {
            newSelection[assistant._id] = selected;
        });
        updateFormState({ ...formState, selectedAssistants: newSelection });
    };

    const handleSelectAssistant = (id: string, selected: boolean) => {
        updateFormState({
            ...formState,
            selectedAssistants: {
                ...formState.selectedAssistants,
                [id]: selected
            }
        });
    };

    const areAllSelected = () => {
        const totalAssistants = assistants.length;
        if (totalAssistants === 0) return false;
        const selectedCount = Object.values(formState.selectedAssistants).filter(v => v).length;
        return selectedCount === totalAssistants;
    };

    const handleSaveMidpoint = async () => {
        try {
            let metadata = adminMetadata;
            if (!metadata) {
                metadata = await getOrCreateAdminMetadata();
            }
            
            await updateAdminMetadata({
                id: metadata._id,
                midpoint: formState.midpoint
            });
            
            setAlertMessage('Midpoint saved successfully');
            setShowAlert(true);
            setSuccess(true);
        } catch (error) {
            setAlertMessage('Error saving midpoint');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    const handleSaveBody = async () => {
        try {
            let metadata = adminMetadata;
            if (!metadata) {
                metadata = await getOrCreateAdminMetadata();
            }
            
            await updateAdminMetadata({
                id: metadata._id,
                body: formState.body
            });
            
            setAlertMessage('Body saved successfully');
            setShowAlert(true);
            setSuccess(true);
        } catch (error) {
            setAlertMessage('Error saving body');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    return (
        <section className="min-h-screen bg-background pt-24 px-6 flex flex-col justify-start">
            <h1 className="text-3xl font-bold mb-4">Assistant Admin Metadata</h1>
            <div className="flex flex-row gap-4 mb-6">
                <Button color="light" onClick={() => navigate('../')}>
                    Back to admin
                </Button>
            </div>
            <p className="text-gray-600 mb-4">
                Manage global header, midpoint, and footer for assistants. Header and footer can be applied to multiple assistants at once. Midpoint is automatically injected where &lt;midpoint&gt; placeholders are found in assistant instructions.
            </p>

            <div className="grid grid-cols-1 gap-6 mb-8">
                <div>
                    <Field>
                        <Label>Header</Label>
                        <Textarea
                            value={formState.header}
                            onChange={(e) => updateFormState({ ...formState, header: e.target.value })}
                            rows={5}
                            placeholder="Enter header text here."
                        />
                    </Field>
                    <div className="mt-2 flex gap-2">
                        <Button 
                            color="light"
                            onClick={handleSaveHeader}
                        >
                            Save Header
                        </Button>
                        <Button 
                            color="light"
                            onClick={handleApplyHeaderToAssistants}
                            disabled={updating}
                        >
                            Apply Header to Selected
                        </Button>
                    </div>
                </div>

                <div>
                    <Field>
                        <Label>Midpoint</Label>
                        <Textarea
                            value={formState.midpoint}
                            onChange={(e) => updateFormState({ ...formState, midpoint: e.target.value })}
                            rows={5}
                            placeholder="Enter midpoint text here. This will replace <midpoint> placeholders in assistant instructions."
                        />
                    </Field>
                    <div className="mt-2 flex gap-2">
                        <Button 
                            color="light"
                            onClick={handleSaveMidpoint}
                        >
                            Save Midpoint
                        </Button>
                        <Button 
                            color="light"
                            onClick={handleApplyMidpointToAssistants}
                            disabled={updating}
                        >
                            Apply Midpoint to Selected
                        </Button>
                    </div>
                </div>

                <div>
                    <Field>
                        <Label>Body</Label>
                        <Textarea
                            value={formState.body}
                            onChange={(e) => updateFormState({ ...formState, body: e.target.value })}
                            rows={5}
                            placeholder="Enter body text here."
                        />
                    </Field>
                    <div className="mt-2 flex gap-2">
                        <Button 
                            color="light"
                            onClick={handleSaveBody}
                        >
                            Save Body
                        </Button>
                    </div>
                </div>

                <div>
                    <Field>
                        <Label>Footer</Label>
                        <Textarea
                            value={formState.footer}
                            onChange={(e) => updateFormState({ ...formState, footer: e.target.value })}
                            rows={5}
                            placeholder="Enter footer text here."
                        />
                    </Field>
                    <div className="mt-2 flex gap-2">
                        <Button 
                            color="light"
                            onClick={handleSaveFooter}
                        >
                            Save Footer
                        </Button>
                        <Button 
                            color="light"
                            onClick={handleApplyFooterToAssistants}
                            disabled={updating}
                        >
                            Apply Footer to Selected
                        </Button>
                    </div>
                </div>
            </div>

            <div className="mb-4 mt-8">
                <h2 className="text-2xl font-bold mb-4">Apply to Assistants</h2>
                <p className="text-gray-600 mb-4">
                    Select assistants to apply the header and footer to. You can apply them individually using the buttons above,
                    or apply both at once using the button below.
                </p>
            </div>

            <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                    <Input
                        type="text"
                        placeholder="Filter assistants by name..."
                        value={formState.nameFilter}
                        onChange={(e) => updateFormState({ ...formState, nameFilter: e.target.value })}
                    />
                </div>
                <div className="text-sm text-gray-600">
                    {selectedCount} of {filteredAssistants.length} selected
                </div>
            </div>

            <div className="flex justify-between mb-4">
                <div>
                    <Button 
                        color="light" 
                        onClick={() => handleSelectAll(true)}
                        className="mr-2"
                    >
                        Select All
                    </Button>
                    <Button 
                        color="light" 
                        onClick={() => handleSelectAll(false)}
                    >
                        Deselect All
                    </Button>
                </div>
                <div>
                    <Button 
                        color="light" 
                        onClick={handleApplyEverythingToAssistants}
                        disabled={updating}
                        className="ml-2"
                    >
                        {updating ? 'Applying...' : 'Apply Both to Selected'}
                    </Button>
                </div>
            </div>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader className="w-16">
                            <input 
                                type="checkbox" 
                                checked={areAllSelected()}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                        </TableHeader>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Type</TableHeader>
                        <TableHeader>Modified</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredAssistants.map(assistant => (
                        <TableRow key={assistant._id}>
                            <TableCell>
                                <input 
                                    type="checkbox" 
                                    checked={formState.selectedAssistants[assistant._id] || false}
                                    onChange={(e) => handleSelectAssistant(assistant._id, e.target.checked)}
                                />
                            </TableCell>
                            <TableCell>{assistant.name}</TableCell>
                            <TableCell>{assistant.typeName || "Standard"}</TableCell>
                            <TableCell>
                                {assistant.last_modified_timestamp 
                                    ? new Date(assistant.last_modified_timestamp).toLocaleDateString() 
                                    : ''}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
                <AlertTitle>{success ? "Success" : "Error"}</AlertTitle>
                <AlertDescription>{alertMessage}</AlertDescription>
                <AlertActions>
                    <Button plain onClick={() => setShowAlert(false)}>
                        Ok
                    </Button>
                </AlertActions>
            </Alert>
        </section>
    );
}; 